// @angular
import { Injectable } from "@angular/core";
import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Observable } from "rxjs";
// Translate
import { TranslateService } from "@ngx-translate/core";
// Own services
import { HttpRequestService } from "./HttpRequestService.service";
import { HttpRequestQueueService } from "./HttpRequestQueueService.service";
import { ToastService } from "../ToastService.service";
// Variables
import { ROUTES_WITHOUT_SPINNER, ROUTES_WITH_OPTIONAL_SPINNER } from "./TIMEOUTS";
import { AQUALIA_ROUTES } from "./AQUALIA_ROUTES";
import { isApiUrl, isHostOrSubdomain } from "./api-url.util";
// Environment variable
import { ENVIRONMENT } from "../../../../environments/environment";

/** Adds backend authentication, applies partner-host routing, and delegates HTTP request handling. */
@Injectable()
export class HttpRequestInterceptorService implements HttpInterceptor {
  /***************************************************************************/
  // Constructor
  /***************************************************************************/

  constructor(
    private readonly HttpRequestService: HttpRequestService,
    private readonly HttpRequestQueueService: HttpRequestQueueService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // Interceptor
  /***************************************************************************/

  /** Adds tokens only to non-login backend calls and routes queued and ordinary requests. */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get token
    let token: string = this.HttpRequestService.getToken();

    // Token only to our own backend (never external APIs) and never on login
    let MODIFIED_REQUEST: HttpRequest<any> =
      isApiUrl(req.url, ENVIRONMENT.API_ENDPOINT) && !req.url.includes("/login")
        ? req.clone({ headers: req.headers.append("Authorization", "Token " + token) })
        : req;

    // Aqualia route check by hostname (href can include route/query controlled by a link)
    let aqualiaRoute = AQUALIA_ROUTES.find(route =>
      isHostOrSubdomain(window.location.hostname, route.url)
    );

    if (aqualiaRoute?.server) {
      MODIFIED_REQUEST = MODIFIED_REQUEST.clone({
        url: req.url.replace("lorawansrv.arsonmetering.com", aqualiaRoute.server)
      });
    }

    // Resolve timeout for the route (null = no timeout)
    let requestTimeout = this.HttpRequestService.setUrlTimeout(req.url);

    // Check if this is a queued request
    if (req.url.includes("/queue")) {
      const MODIFIED_URL_REQUEST: HttpRequest<any> = MODIFIED_REQUEST.clone({
        url: req.url.replace("/queue", "")
      });
      return this.HttpRequestQueueService.request(MODIFIED_URL_REQUEST, next, requestTimeout);
    } else {
      // Check if the route should show the loading spinner
      let spinnerActive = !this.HttpRequestService.urlFilter(req.url, ROUTES_WITHOUT_SPINNER);

      // Optional loading spinner
      if (this.HttpRequestService.urlFilter(req.url, ROUTES_WITH_OPTIONAL_SPINNER)) {
        this.ToastService.fireAlertWithOptions(
          "question",
          this.translate.instant("timeout-question")
        ).then((userConfirmation: boolean) => {
          spinnerActive = userConfirmation;
          // Request
          return this.HttpRequestService.request(
            req,
            next,
            MODIFIED_REQUEST,
            requestTimeout,
            spinnerActive
          );
        });
      } else {
        // Request
        return this.HttpRequestService.request(
          req,
          next,
          MODIFIED_REQUEST,
          requestTimeout,
          spinnerActive
        );
      }
    }
  }
}
